import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Volet roulant qui ne remonte plus' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Volet roulant qui ne remonte plus dans l\'Hérault',
  description: 'Votre volet roulant ne remonte plus ? Causes (condensateur, fins de course, sangle, lames) selon qu\'il soit électrique ou manuel. Diagnostic et solutions dans l\'Hérault.',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/volet-roulant-ne-remonte-plus/',
  datePublished: '2026-04-12',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Que faire si mon volet roulant électrique ne remonte plus ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vérifiez d\'abord l\'alimentation électrique : le disjoncteur n\'a-t-il pas sauté ? Testez ensuite la télécommande (piles). Si le moteur émet un bourdonnement mais ne tourne pas, le condensateur est probablement HS. Dans ce cas, coupez l\'alimentation et appelez un professionnel : un condensateur se remplace facilement mais nécessite des précautions électriques.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pourquoi la sangle de mon volet roulant manuel est-elle molle ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Une sangle molle ou qui ne s\'enroule plus correctement indique généralement un problème au niveau du mécanisme d\'enrouleur : ressort cassé, axe de l\'enrouleur bloqué, ou sangle elle-même effilochée et sortie de son guide. Dans la plupart des cas, il faut changer la sangle et réviser le mécanisme.',
      },
    },
    {
      '@type': 'Question',
      name: 'Faut-il remplacer tout le volet roulant s\'il ne remonte plus ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Rarement. Dans la très grande majorité des cas, la panne est localisée sur un composant spécifique : condensateur, moteur, sangle, fins de course. Le remplacement de ce composant suffit à restaurer le fonctionnement normal du volet, sans qu\'il soit nécessaire de changer l\'ensemble du tablier ou du coffre.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/volet-roulant-ne-remonte-plus/', name: 'Volet roulant qui ne remonte plus' } },
  ],
};

export default function ArticleVoletNeRemontePlus() {
  return (
    <>
      <SEO
        heroImage="/images/blog-volet-remonte.jpg"
        title="Volet roulant qui ne remonte plus : causes et solutions"
        description="Volet roulant bloqué en bas, ne remonte plus dans l'Hérault ? Électrique ou manuel, causes et diagnostic. Intervention 24-48h, devis gratuit. DFPRO."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/volet-roulant-ne-remonte-plus/"
      />
      <BlogArticleLayout
        title="Volet roulant qui ne remonte plus dans l&apos;Hérault"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="12 avril 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Vous appuyez sur l&apos;interrupteur, vous tournez la manivelle ou vous tirez sur la sangle, et rien ne se passe. Votre volet roulant reste désespérément en position basse. C&apos;est une panne frustrante, surtout quand elle survient le matin et que votre pièce reste plongée dans le noir. Heureusement, les causes sont bien connues des professionnels et se résolvent dans la grande majorité des cas sans remplacer l&apos;ensemble du volet. Voyons ce qui peut empêcher un volet roulant de remonter, et comment réagir quand cela vous arrive dans l&apos;Hérault.
        </p>

        <h2>Volet roulant électrique : les causes électriques</h2>
        <p>
          Sur un volet motorisé, le problème vient le plus souvent de l&apos;alimentation électrique ou du moteur lui-même. Avant toute chose, vérifiez que le disjoncteur correspondant n&apos;a pas sauté. C&apos;est bête, mais ça arrive — un orage, une micro-coupure, et le volet ne répond plus. Si l&apos;alimentation est bonne, écoutez : un moteur qui bourdonne sans tourner indique un <strong>condensateur de démarrage hors service</strong>. Ce petit composant électrique fournit l&apos;impulsion nécessaire au démarrage du moteur. Avec le temps — et la chaleur, fréquente dans l&apos;Hérault en été — les condensateurs se détériorent et ne stockent plus assez de charge pour lancer la rotation. Le remplacement d&apos;un condensateur est une opération rapide pour un technicien, mais il ne faut pas la tenter soi-même : un condensateur peut conserver une charge électrique dangereuse même une fois le volet débranché.
        </p>

        <h2>Les fins de course : des butées invisibles mais essentielles</h2>
        <p>
          Tout moteur de volet roulant est équipé de fins de course, des dispositifs mécaniques ou électroniques qui indiquent au moteur où s&apos;arrêter en position haute et basse. Quand un fin de course se dérègle, le moteur peut « croire » que le volet est déjà en haut et refuser de remonter, ou au contraire continuer à forcer en position haute et abîmer le mécanisme. Le réglage des fins de course nécessite de savoir où se trouvent les vis de réglage (généralement dans le coffre ou sur la tête du moteur) et de comprendre leur fonctionnement. C&apos;est une intervention courante que nos techniciens réalisent en moins de 30 minutes à Montpellier, Sète et dans toutes les communes de l&apos;Hérault.
        </p>

        {/* CTA milieu */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Votre volet roulant ne remonte plus dans l&apos;Hérault ?
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            Nos techniciens diagnostiquent et réparent votre <Link to="/volet-roulant-ne-remonte-plus-herault/" className="text-accent-500 font-semibold hover:underline">volet roulant qui ne remonte plus</Link> sous 24 à 48h.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Volet roulant manuel : sangle, enrouleur et treuil</h2>
        <p>
          Si votre volet est manuel, c&apos;est-à-dire actionné par une sangle ou une manivelle, les causes de blocage sont mécaniques. La plus fréquente est une <strong>sangle cassée ou sortie de son enrouleur</strong>. La sangle, qui s&apos;enroule autour d&apos;un axe dans le coffre, peut s&apos;effilocher avec le temps, se coincer, ou tout simplement se rompre si elle a été tirée trop fort. Dans ce cas, le <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">remplacement de la sangle</Link> et la révision du mécanisme d&apos;enroulement suffisent à restaurer le fonctionnement.
        </p>
        <p>
          Autre possibilité : le treuil — le mécanisme qui transforme la traction de la sangle en rotation de l&apos;axe — est grippé ou cassé. C&apos;est un problème plus lourd, qui peut justifier une <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation du volet roulant</Link> comme alternative au remplacement du treuil. La motorisation transforme votre volet manuel en volet électrique, avec télécommande, pour un confort d&apos;utilisation bien supérieur.
        </p>

        <h2>Les lames du tablier : une cause mécanique à ne pas négliger</h2>
        <p>
          Le tablier — la partie visible du volet composée de lames articulées — peut aussi être à l&apos;origine du blocage. Une lame tordue, fendue ou sortie de ses glissières latérales bloquera la remontée du volet, quel que soit l&apos;état du moteur ou de la sangle. Dans l&apos;Hérault, les volets exposés plein sud subissent des écarts de température importants qui peuvent dilater et déformer les lames PVC au fil des années. Une inspection visuelle du tablier, lame par lame, permet d&apos;identifier ce type de problème. Une ou deux lames abîmées peuvent être remplacées individuellement, sans changer l&apos;ensemble du tablier.
        </p>

        <h2>Le geste à ne pas faire</h2>
        <p>
          Quand un volet ne remonte pas, la tentation est grande de forcer : tirer plus fort sur la sangle, appuyer frénétiquement sur l&apos;interrupteur, essayer de soulever le tablier à la main. <strong>Aucune de ces méthodes n&apos;est recommandée.</strong> Forcer sur la sangle peut la rompre ou casser le mécanisme d&apos;enrouleur. Multiplier les tentatives électriques peut griller le moteur si le problème vient d&apos;un condensateur défaillant. Soulever le tablier manuellement peut le faire sortir de ses coulisses et aggraver la situation. Le meilleur réflexe est de couper l&apos;alimentation (pour un volet électrique) et d&apos;appeler un professionnel.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Un volet roulant bloqué en bas, est-ce une urgence ?</h3>
        <p>
          Ce n&apos;est pas une urgence vitale, mais c&apos;est une gêne réelle : pièce sans lumière naturelle, impossibilité d&apos;aérer, sentiment d&apos;insécurité si le volet ne protège plus votre fenêtre. Dans l&apos;Hérault, où le climat est ensoleillé une grande partie de l&apos;année, un volet bloqué en bas peut également entraîner une surchauffe de la pièce. Nous intervenons sous 24 à 48h pour rétablir le fonctionnement.
        </p>

        <h3>Peut-on motoriser un volet manuel qui ne remonte plus ?</h3>
        <p>
          Oui, c&apos;est même souvent l&apos;occasion idéale. Plutôt que de réparer un mécanisme manuel vieillissant, la motorisation apporte un confort moderne (télécommande, programmation horaire, détection d&apos;obstacle) pour un investissement raisonnable. Nos techniciens peuvent vous conseiller sur la faisabilité et vous établir un devis.
        </p>
      </BlogArticleLayout>
    </>
  );
}